import { useEffect, useState } from "react";
import {
    useCreate,
    useCustomMutation,
    useInvalidate,
    useList,
    useNotification,
    useOne,
    useTranslate,
    useUpdate,
} from "@refinedev/core";
import { message } from "antd";
import { axiosInstance, TOKEN_KEY } from "../../../authProvider";
import { IAutomationTask, AutomationTaskStep, AutomationTaskStatus } from "../../../models/automation-task";
import { OrderStatus } from "../../../models/order";
import { Tag, Button, Space } from "antd";
import {
    InfoCircleOutlined,
    DownloadOutlined,
    SendOutlined,
} from "@ant-design/icons";
import React from "react";

interface UseOrderAutomationParams {
    record: any;
    attachments: any[];
    orderId: string | number | undefined;
    apiUrl: string;
    refetchOrder: () => void;
    isAdmin: boolean;
    setRecord: React.Dispatch<React.SetStateAction<any>>;
    setOrderStatus: React.Dispatch<React.SetStateAction<string>>;
    markMessagesRead: () => void;
}

/** S3/DO presigned URLs only — file-backend URLs (e.g. localhost:3001) need Bearer auth. */
const isPresignedDownloadUrl = (url: string): boolean =>
    url.includes("digitaloceanspaces.com") ||
    url.includes("amazonaws.com") ||
    url.includes("X-Amz-Algorithm");

const downloadBlobWithAuth = async (url: string): Promise<Blob> => {
    const response = await fetch(url, { headers: getDownloadAuthHeaders() });
    if (!response.ok) {
        throw new Error(`Download failed with status: ${response.status}`);
    }
    return response.blob();
};

const resolveDownloadUrl = (url: string, apiBaseUrl: string): string =>
    url.startsWith("/") ? `${apiBaseUrl}${url}` : url;

const getDownloadAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const useOrderAutomation = ({
    record,
    attachments,
    orderId,
    apiUrl,
    refetchOrder,
    isAdmin,
    setRecord,
    setOrderStatus,
    markMessagesRead,
}: UseOrderAutomationParams) => {
    const _t = useTranslate();
    const { open } = useNotification();
    const invalidate = useInvalidate();
    const { mutate: update } = useUpdate();
    const { mutate: createResource } = useCreate();

    // Automation tasks list
    const { data: automationTasksData, refetch: refetchAutomationTasks } = useList<IAutomationTask>({
        resource: "automation-tasks",
        filters: [{ field: "orderId", operator: "eq", value: orderId }],
        queryOptions: { enabled: !!orderId },
        pagination: { mode: "off" },
    });

    // Rerun task polling
    const [rerunningTaskId, setRerunningTaskId] = useState<number | null>(null);
    const { mutate: mutateRerunTask } = useCustomMutation();

    const { data: rerunTaskData } = useOne<IAutomationTask>({
        resource: "automation-tasks",
        id: rerunningTaskId ?? "",
        queryOptions: {
            enabled: rerunningTaskId != null,
            refetchInterval: 3000,
        },
    });

    // Analysis modal state
    const [analysisModalVisible, setAnalysisModalVisible] = useState(false);
    const [analysisResult, setAnalysisResult] = useState<any>(null);

    // File details modal state
    const [selectedFileData, setSelectedFileData] = useState<any>(null);
    const [fileDetailsModalVisible, setFileDetailsModalVisible] = useState(false);

    // Assembly files modal state
    const [assemblyFilesModalVisible, setAssemblyFilesModalVisible] = useState(false);
    const [assemblyFiles, setAssemblyFiles] = useState<{
        ecuxFileId: string;
        modifiedBinFileId: string;
        finalOutFileName: string;
        preEncrypted: boolean;
    } | null>(null);
    const [assemblyFileUrls, setAssemblyFileUrls] = useState<{ [key: string]: string }>({});
    const [assemblyFilesLoading, setAssemblyFilesLoading] = useState<{ [key: string]: boolean }>({});

    // Assemble state
    const [isAssembling, setIsAssembling] = useState(false);
    const [creatingAttachment, setCreatingAttachment] = useState(false);

    // Sync automation tasks into record
    useEffect(() => {
        if (!automationTasksData?.data) return;
        setRecord((prevRecord: any) => {
            if (!prevRecord) return prevRecord;
            return {
                ...prevRecord,
                automationTasks: automationTasksData.data ?? prevRecord.automationTasks,
            };
        });
    }, [automationTasksData?.data]);

    // Stop polling when rerun task finishes
    const rerunTask = rerunTaskData?.data;
    useEffect(() => {
        if (rerunningTaskId == null || !rerunTask || rerunTask.id !== rerunningTaskId) return;
        if (
            rerunTask.status === AutomationTaskStatus.completed ||
            rerunTask.status === AutomationTaskStatus.failed
        ) {
            setRerunningTaskId(null);
            refetchAutomationTasks();
            open?.({
                type: "success",
                message: _t("orders.automationTasks.rerunSuccess"),
            });
        }
    }, [rerunningTaskId, rerunTask]);

    const handleRerunTask = (task: IAutomationTask) => {
        setRerunningTaskId(task.id);
        mutateRerunTask(
            {
                url: `${apiUrl}/automation-tasks/${task.id}/rerun`,
                method: "post",
                values: {},
            },
            {
                onSuccess: () => {
                    refetchOrder();
                    open?.({
                        type: "success",
                        message: _t("orders.automationTasks.rerunStarted"),
                    });
                },
                onError: (err: any) => {
                    setRerunningTaskId(null);
                    const status = err?.response?.status;
                    if (status === 409) {
                        open?.({
                            type: "error",
                            message: _t("orders.automationTasks.rerunAlreadyInProgress"),
                        });
                    } else {
                        open?.({
                            type: "error",
                            message: _t("notifications.error"),
                        });
                    }
                },
            }
        );
    };

    const hasAvailableAutomation = () => {
        if (!isAdmin || !record?.automationTasks) return false;
        const analyzeTask = record.automationTasks.find(
            (task: IAutomationTask) =>
                task.step === AutomationTaskStep.analyze &&
                task.status === AutomationTaskStatus.completed
        );
        const assemblyTask = record.automationTasks.find(
            (task: IAutomationTask) =>
                task.step === AutomationTaskStep.assembly &&
                task.status === AutomationTaskStatus.completed
        );
        return 
        (
            analyzeTask?.metadata?.analysisResult?.projects &&
            Array.isArray(analyzeTask.metadata.analysisResult.projects) &&
            analyzeTask.metadata.analysisResult.projects.length > 0 &&
            record.status !== OrderStatus.completed &&
            record.status !== OrderStatus.sav
        ) || assemblyTask;
    };

    const handleOpenAutomationModal = () => {
        if (!record?.automationTasks) return;
        const analyzeTask = record.automationTasks.find(
            (task: IAutomationTask) =>
                task.step === AutomationTaskStep.analyze &&
                task.status === AutomationTaskStatus.completed
        );
        if (analyzeTask?.metadata?.analysisResult) {
            setAnalysisResult(analyzeTask.metadata.analysisResult);
            setAnalysisModalVisible(true);
        }
    };

    const handleRowClick = (row: any) => {
        setSelectedFileData(row.fileData);
        setFileDetailsModalVisible(true);
    };

    const handleFileDownload = async (fileData?: any) => {
        const file = fileData ?? selectedFileData;
        if (file?.downloadUrl) {
            try {
                const downloadUrl = resolveDownloadUrl(file.downloadUrl, apiUrl);
                const fileName =
                    file.binaryPath?.split("/").pop() || file.type || "file";

                if (isPresignedDownloadUrl(downloadUrl)) {
                    const downloadResponse = await fetch(downloadUrl);
                    if (!downloadResponse.ok) {
                        throw new Error(
                            `Download failed with status: ${downloadResponse.status}`
                        );
                    }
                    triggerBlobDownload(await downloadResponse.blob(), fileName);
                } else {
                    triggerBlobDownload(
                        await downloadBlobWithAuth(downloadUrl),
                        fileName
                    );
                }
                message.success(_t("automation.messages.downloadStarted"));
            } catch (error: any) {
                console.error("Download error:", error);
                message.error(_t("automation.errors.downloadFailed"));
            }
        } else {
            message.error(_t("automation.errors.downloadUrlNotAvailable"));
        }
    };

    const handleAssemble = async (recordFile: any) => {
        if (!attachments || attachments.length === 0) {
            message.error(_t("orders.errors.noOriginalFileFromAttachments"));
            return;
        }

        console.log("recordFile", recordFile);
        setIsAssembling(true);
        try {
            let originalAttachment = attachments[0];
            console.log("Attachments", attachments);
            if (!originalAttachment?.file?.path) {
                message.error(_t("orders.errors.originalFilePathNotFound"));
                setIsAssembling(false);    
                return;
            }

            const decryptTask = record.automationTasks?.find(
                (task: IAutomationTask) =>
                    task.step === AutomationTaskStep.decrypt &&
                    task.status === AutomationTaskStatus.completed
            );

            if (decryptTask) {
                console.info("Original file was encrypted");
                // Find attachment that has metadata include {"source":"automation_decrypt"}
                const decryptedAttachment = attachments.find(
                    (att: any) => att.metadata?.includes('automation_decrypt')
                );
                if (!decryptedAttachment) {
                    message.error(_t("orders.errors.decryptedFilePathNotFound"));
                    console.error("Decrypted file not in attachments");
                    setIsAssembling(false);
                    return;
                } else {
                    originalAttachment = decryptedAttachment;
                }
            } else {
                console.info("Original file was not encrypted");
            }

            //const fileId = decryptTask?.metadata?.fileId ?? originalAttachment.file.id;
            const fileId = originalAttachment.file.id;
            console.log(`Input File ID to be used for assembly is ${fileId}`)
            
            //console.log("decryptTask", decryptTask);
            //console.log("decryptTask.metadata.decryptedFileId", decryptTask?.metadata?.decryptedFileId);
            //console.log("originalAttachment.file.id", originalAttachment.file.id);
            

            const analyzeTask = record.automationTasks?.find(
                (task: IAutomationTask) =>
                    task.step === AutomationTaskStep.analyze &&
                    task.status === AutomationTaskStatus.completed
            );
            
            if (!analyzeTask) {
                message.error(_t("orders.errors.analyzeTaskNotFound"));
                setIsAssembling(false);
                return;
            }

            const uploadedFileAnalysis = analyzeTask?.metadata?.analysisResult;
            const project = analysisResult?.projects?.find(
                (p: any) => p.projectRef === recordFile.projectRef
            );
            if (!project) {
                message.error(_t("automation.errors.projectNotFound"));
                setIsAssembling(false);
                return;
            }

            const originalFile = project.files?.find((f: any) => f.type === "Original");
            if (!originalFile) {
                message.error(_t("automation.errors.originalFileNotFound"));
                setIsAssembling(false);
                return;
            }

            const projectInfo = {
                vehicleType: originalFile.vehicleType,
                vehicleProducer: originalFile.vehicleProducer,
                vehicleSeries: originalFile.vehicleSeries,
                vehicleBuild: originalFile.vehicleBuild,
                vehicleModel: originalFile.vehicleModel,
                vehicleModelyear: originalFile.vehicleModelyear,
                ecuUse: originalFile.ecuUse,
                ecuProducer: originalFile.ecuProducer,
                ecuBuild: originalFile.ecuBuild,
                ecuSoftwareVersion: originalFile.ecuSoftwareVersion,
                engineType: originalFile.engineType,
                engineDisplacement: originalFile.engineDisplacement,
                readingHardware: originalFile.readingHardware,
                projectType: originalFile.projectType,
            };

            const analysisSource = uploadedFileAnalysis || analysisResult;
            let targetSrcAnalysis = null;
            if (analysisSource?.Blocks && Array.isArray(analysisSource.Blocks)) {
                targetSrcAnalysis = {
                    Blocks: analysisSource.Blocks.map((block: any) => ({
                        Block: {
                            BlockIdentifier: block.Block?.BlockIdentifier,
                            BlockName: block.Block?.BlockName,
                            PhysicalAddress: block.Block?.PhysicalAddress,
                            LogicalAddress: block.Block?.LogicalAddress,
                            PhysicalToLogicalOffset: block.Block?.PhysicalToLogicalOffset,
                            Endblock_PhysicalAddress: block.Block?.Endblock_PhysicalAddress,
                            Endblock_LogicalAddress: block.Block?.Endblock_LogicalAddress,
                            BlockSize: block.Block?.BlockSize,
                            BlockType: block.Block?.BlockType,
                        },
                        Hash: block.Hash,
                    })),
                };
            }

            let patchSrcAnalysis = null;
            if (originalFile.blocks) {
                patchSrcAnalysis = {
                    Blocks: originalFile.blocks.map((block: any) => ({
                        Block: { ...block, BlockType: Number(block.BlockType) },
                        Hash: block.Hash,
                    })),
                };
            } else {
                message.error(_t("orders.errors.originalFileBlocksNotFound"));
                setIsAssembling(false);
                return;
            }

            const clickedFile = recordFile.fileData;
            let patchModifiedAnalysis = null;
            if (clickedFile.blocks) {
                patchModifiedAnalysis = {
                    Blocks: clickedFile.blocks.map((block: any) => ({
                        Block: { ...block, BlockType: Number(block.BlockType) },
                        Hash: block.Hash,
                    })),
                };
            }

            const payload = {
                orderId: record.id,
                projectInfo,
                files: [
                    { type: "target_src", fileId, analysis: targetSrcAnalysis },
                    { type: "patch_src", downloadUrl: originalFile.downloadUrl, analysis: patchSrcAnalysis },
                    { type: "patch_modified", downloadUrl: clickedFile.downloadUrl, analysis: patchModifiedAnalysis},
                ],
            };

            console.log("Assembly Payload", payload);

            const assembleResponse = await axiosInstance.post("/file-assembler/assemble", payload);
            setFileDetailsModalVisible(false);
            refetchAutomationTasks();

            if (assembleResponse.data?.ecuxDecryptedFilePath) {
                try {
                    const { ecuxDecryptedFilePath, finalFileName } = assembleResponse.data;
                    const downloadResponse = await fetch(ecuxDecryptedFilePath);
                    if (!downloadResponse.ok) {
                        throw new Error(`Download failed with status: ${downloadResponse.status}`);
                    }
                    const blob = await downloadResponse.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    const urlParts = ecuxDecryptedFilePath.split("/");
                    link.download =
                        finalFileName || urlParts[urlParts.length - 1].split("?")[0] || "assembled_file.ecux";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    message.success(_t("automation.messages.assembleSuccessDownloaded"));
                } catch (downloadError: any) {
                    console.error("Download error:", downloadError);
                    window.open(assembleResponse.data.ecuxDecryptedFilePath, "_blank");
                    message.warning(_t("automation.messages.assembleSuccessOpenTab"));
                }
            } else {
                message.success(_t("automation.messages.assembleSuccess"));
            }
        } catch (error: any) {
            console.error("Assemble error:", error);
            message.error(error.response?.data?.message || _t("automation.errors.assembleFailed"));
        } finally {
            setIsAssembling(false);
        }
    };

    const handleOpenAssemblyFilesModal = (task: IAutomationTask) => {
        if (task.metadata?.ecuxFileId && task.metadata?.modifiedBinFileId) {
            setAssemblyFiles({
                ecuxFileId: task.metadata.ecuxFileId,
                modifiedBinFileId: task.metadata.modifiedBinFileId,
                finalOutFileName:
                    task.metadata.finalOutFileName ?? task.metadata.finalFileName ?? "file.bin",
                preEncrypted: task.metadata.outPreEncrypted === true,
            });
            setAssemblyFilesModalVisible(true);
            setAssemblyFileUrls({});
            setAssemblyFilesLoading({});
        }
    };

    const fetchAssemblyFileDownloadUrl = async (fileId: string): Promise<string | null> => {
        try {
            setAssemblyFilesLoading((prev) => ({ ...prev, [fileId]: true }));
            const response = await axiosInstance.get(`/files/download-url/${fileId}`);
            const downloadUrl = response.data?.downloadUrl;
            if (downloadUrl) {
                setAssemblyFileUrls((prev) => ({ ...prev, [fileId]: downloadUrl }));
                return downloadUrl;
            }
            return null;
        } catch (error: any) {
            console.error("Error fetching download URL:", error);
            message.error(_t("automation.errors.downloadUrlNotAvailable"));
            return null;
        } finally {
            setAssemblyFilesLoading((prev) => ({ ...prev, [fileId]: false }));
        }
    };

    const handleAssemblyFileDownload = async (fileId: string, fileName: string) => {
        try {
            let downloadUrl: string | undefined = assemblyFileUrls[fileId];
            if (!downloadUrl) {
                const fetched = await fetchAssemblyFileDownloadUrl(fileId);
                if (!fetched) {
                    message.error(_t("automation.errors.downloadUrlNotAvailable"));
                    return;
                }
                downloadUrl = fetched;
            }
            if (!downloadUrl) {
                message.error(_t("automation.errors.downloadUrlNotAvailable"));
                return;
            }

            const resolvedUrl = resolveDownloadUrl(downloadUrl, apiUrl);

            if (isPresignedDownloadUrl(resolvedUrl)) {
                try {
                    const downloadResponse = await fetch(resolvedUrl);
                    if (!downloadResponse.ok) {
                        throw new Error(`Download failed with status: ${downloadResponse.status}`);
                    }
                    triggerBlobDownload(await downloadResponse.blob(), fileName);
                    message.success(_t("automation.messages.downloadStarted"));
                } catch (fetchError: any) {
                    console.warn("Fetch failed, opening URL directly:", fetchError);
                    window.open(resolvedUrl, "_blank");
                    message.warning(_t("automation.messages.assembleSuccessOpenTab"));
                }
            } else {
                triggerBlobDownload(
                    await downloadBlobWithAuth(resolvedUrl),
                    fileName
                );
                message.success(_t("automation.messages.downloadStarted"));
            }
        } catch (error: any) {
            console.error("Download error:", error);
            message.error(_t("automation.errors.downloadFailed"));
        }
    };

    const handleCreateAttachmentFromModifiedBin = () => {
        if (!assemblyFiles?.modifiedBinFileId || !record?.id) {
            message.error(_t("orders.errors.missingFileOrOrderInfo"));
            return;
        }

        const existingAttachment = record?.attachments?.find(
            (att: any) =>
                att.fileId === assemblyFiles.modifiedBinFileId && att.direction === "out"
        );

        if (existingAttachment) {
            setCreatingAttachment(true);
            update(
                { resource: "orders", values: { status: OrderStatus.completed }, id: record.id },
                {
                    onSuccess: () => {
                        markMessagesRead();
                        setOrderStatus(OrderStatus.completed);
                        setRecord((prev: any) => ({ ...prev, status: OrderStatus.completed }));
                        invalidate({ resource: "orders", invalidates: ["detail"], id: record?.id });
                        setCreatingAttachment(false);
                        setAssemblyFilesModalVisible(false);
                        message.success(_t("orders.messages.orderMarkedCompletedAttachmentExists"));
                    },
                    onError: () => {
                        setCreatingAttachment(false);
                        message.error(_t("orders.errors.failedToUpdateOrderStatus"));
                    },
                }
            );
            return;
        }

        setCreatingAttachment(true);
        const attachment = {
            name: assemblyFiles.finalOutFileName,
            fileId: assemblyFiles.modifiedBinFileId,
            metadata: JSON.stringify({
                type: "bin",
                preEncrypted: assemblyFiles.preEncrypted === true,
            }),
            direction: "out",
            orderId: `${record.id}`,
        };

        createResource(
            { resource: "attachments", values: attachment },
            {
                onSuccess: () => {
                    update(
                        { resource: "orders", values: { status: OrderStatus.completed }, id: record.id },
                        {
                            onSuccess: () => {
                                markMessagesRead();
                                setOrderStatus(OrderStatus.completed);
                                setRecord((prev: any) => ({ ...prev, status: OrderStatus.completed }));
                                invalidate({ resource: "orders", invalidates: ["detail"], id: record?.id });
                                setCreatingAttachment(false);
                                setAssemblyFilesModalVisible(false);
                                message.success(_t("orders.messages.attachmentCreatedOrderCompleted"));
                            },
                            onError: () => {
                                setCreatingAttachment(false);
                                message.error(_t("orders.errors.failedToUpdateOrderStatus"));
                            },
                        }
                    );
                },
                onError: (error: any) => {
                    setCreatingAttachment(false);
                    if (error?.response?.status === 400 || error?.response?.status === 409) {
                        update(
                            { resource: "orders", values: { status: OrderStatus.completed }, id: record.id },
                            {
                                onSuccess: () => {
                                    markMessagesRead();
                                    setOrderStatus(OrderStatus.completed);
                                    setRecord((prev: any) => ({ ...prev, status: OrderStatus.completed }));
                                    invalidate({ resource: "orders", invalidates: ["detail"], id: record?.id });
                                    setAssemblyFilesModalVisible(false);
                                    message.warning(_t("orders.messages.attachmentExistsOrderCompleted"));
                                },
                                onError: () => {
                                    message.error(_t("orders.errors.failedToUpdateOrderStatus"));
                                },
                            }
                        );
                    } else {
                        message.error(_t("orders.errors.failedToCreateAttachment"));
                    }
                },
            }
        );
    };

    const getTableData = () => {
        if (!analysisResult?.projects || !Array.isArray(analysisResult.projects)) return [];
        return analysisResult.projects
            .filter((project: any) => project.files && Array.isArray(project.files))
            .map((project: any) => ({
                key: `project-${project.projectRef}`,
                projectRef: project.projectRef,
                type: "",
                tags: [],
                fileData: null,
                children: project.files.map((file: any) => ({
                    key: file._id || `${project.projectRef}-${file.type}`,
                    projectRef: project.projectRef,
                    type: file.type || "",
                    tags: file.tags || [],
                    fileData: file,
                })),
            }));
    };

    const tableColumns = [
        {
            title: _t("orders.automationTasks.matchedFiles.projectRef"),
            dataIndex: "projectRef",
            key: "projectRef",
            render: (text: string, row: any) => {
                if (!row.fileData) {
                    return (
                        <span className="font-semibold">
                            {text} ({row.children?.length || 0}{" "}
                            {_t("orders.automationTasks.matchedFiles.files")})
                        </span>
                    );
                }
                return text;
            },
        },
        {
            title: _t("orders.automationTasks.matchedFiles.type"),
            dataIndex: "type",
            key: "type",
            render: (text: string, row: any) => {
                if (!row.fileData) return <span className="text-gray-400">-</span>;
                return text || "-";
            },
        },
        {
            title: _t("orders.automationTasks.matchedFiles.tags"),
            dataIndex: "tags",
            key: "tags",
            render: (tags: string[], row: any) => {
                if (!row.fileData) return <span className="text-gray-400">-</span>;
                return tags && tags.length > 0 ? (
                    <div>
                        {tags.map((tag, index) => (
                            <Tag key={index} className="mb-1">
                                {tag}
                            </Tag>
                        ))}
                    </div>
                ) : (
                    <span className="text-gray-400">-</span>
                );
            },
        },
        {
            title: _t("orders.automationTasks.matchedFiles.actions"),
            key: "actions",
            render: (_: any, row: any) => {
                if (!row.fileData) return <span className="text-gray-400">-</span>;
                return (
                    <Space>
                        <Button icon={<InfoCircleOutlined />} onClick={() => handleRowClick(row)} />
                        {row.fileData?.downloadUrl && (
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={() => handleFileDownload(row.fileData)}
                            />
                        )}
                        {attachments && attachments.length > 0 && (
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                onClick={() => handleAssemble(row)}
                                loading={isAssembling}
                                disabled={isAssembling}
                            >
                                {_t("orders.automationTasks.matchedFiles.assemble")}
                            </Button>
                        )}
                    </Space>
                );
            },
        },
    ];

    return {
        automationTasks: automationTasksData?.data,
        refetchAutomationTasks,
        rerunningTaskId,
        handleRerunTask,
        analysisModalVisible,
        setAnalysisModalVisible,
        analysisResult,
        setAnalysisResult,
        hasAvailableAutomation,
        handleOpenAutomationModal,
        assemblyFilesModalVisible,
        setAssemblyFilesModalVisible,
        assemblyFiles,
        assemblyFileUrls,
        assemblyFilesLoading,
        handleOpenAssemblyFilesModal,
        handleAssemblyFileDownload,
        isAssembling,
        handleAssemble,
        handleFileDownload,
        selectedFileData,
        fileDetailsModalVisible,
        setFileDetailsModalVisible,
        handleRowClick,
        creatingAttachment,
        handleCreateAttachmentFromModifiedBin,
        getTableData,
        tableColumns,
    };
};
